/* Generic screen for any F&O backend resource: list (paged, searchable), details, create, edit, delete and the resource's actions.
   Driven by catalog.json (generated from the backend by tools/CatalogGen), so every module has a working page. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton, SkeletonItem, Button, Checkbox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Field, Input, SearchBox, Select, Textarea } from "@fluentui/react-components";
import { Add20Regular, ArrowClockwise20Regular, Delete20Regular, Edit20Regular } from "@fluentui/react-icons";
import { api, errText, Rec } from "./Api/Api";
import css from "./Genericpage.module.css";

/** key, kind, flags ("q" required, "r" read-only), max length, enum values */
type F = [string, string, string, number?, string[]?];
type A = [string, [string, string, string][]];
export interface Res {
  r: string;
  e: string;
  c?: 1;
  s?: string[];
  a?: A[];
  f: F[];
}
export type Catalog = Record<string, Res[]>;

let cache: Promise<Catalog> | null = null;
export const loadCatalog = (): Promise<Catalog> => (cache ??= import("./Catalog.json").then((m) => (m.default ?? m) as unknown as Catalog));

const label = (k: string): string => k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).replace(/\bId\b/, "ID");
const title = (r: Res): string => r.r.split("/").slice(2).join(" / ").replace(/-/g, " ");
const shown = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toLocaleString("en-NG", { maximumFractionDigits: 4 });
  if (typeof v === "string" && /^\d{4}-\d\d-\d\dT/.test(v)) return v.slice(0, 16).replace("T", " ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};
const PREFER = ["number", "code", "name", "title", "fullName", "status"];
const listCols = (r: Res): F[] => {
  const fs = r.f.filter((f) => f[1] !== "guid" || PREFER.includes(f[0]));
  const first = PREFER.flatMap((k) => fs.filter((f) => f[0] === k));
  return [...first, ...fs.filter((f) => !PREFER.includes(f[0]))].slice(0, 8);
};

const Editor: React.FC<{ res: Res; rec: Rec | null; onDone: () => void; onClose: () => void }> = ({ res, rec, onDone, onClose }) => {
  const fields = res.f.filter((f) => !f[2].includes("r"));
  const [v, setV] = useState<Record<string, string | boolean>>(() => Object.fromEntries(fields.map((f) => [f[0], rec ? (rec[f[0]] === null || rec[f[0]] === undefined ? (f[1] === "bool" ? false : "") : f[1] === "bool" ? !!rec[f[0]] : f[1] === "date" ? String(rec[f[0]]).slice(0, 10) : f[1] === "datetime" ? String(rec[f[0]]).slice(0, 16) : String(rec[f[0]])) : f[1] === "bool" ? false : ""])));
  const [extra, setExtra] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const body: Rec = rec ? { ...rec } : ({} as Rec);
    for (const f of fields) {
      const raw = v[f[0]];
      if (f[2].includes("q") && (raw === "" || raw === undefined)) return setErr(`${label(f[0])} is required.`);
      if (f[1] === "bool") body[f[0]] = !!raw;
      else if (raw === "" || raw === undefined) body[f[0]] = f[1] === "text" || f[1] === "enum" ? (rec ? "" : undefined) : null;
      else if (f[1] === "number" || f[1] === "int") body[f[0]] = Number(raw);
      else if (f[1] === "datetime") body[f[0]] = `${String(raw)}:00Z`.replace(/:00:00Z$/, ":00Z");
      else body[f[0]] = raw;
    }
    if (extra.trim()) {
      try {
        Object.assign(body, JSON.parse(extra));
      } catch {
        return setErr("The extra JSON is not valid.");
      }
    }
    setBusy(true);
    setErr("");
    try {
      if (rec) await api.put(`${res.r}/${rec.id}`, body);
      else await api.post(res.r, body);
      onDone();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{rec ? "Edit" : "New"} {title(res)}</DialogTitle>
          <DialogContent>
            <div className={css.form}>
              {fields.map((f) => (
                <div key={f[0]}>
                  <Field label={label(f[0]) + (f[2].includes("q") ? " *" : "")}>
                    {f[1] === "bool" ? (
                      <Checkbox checked={!!v[f[0]]} onChange={(_, d) => setV({ ...v, [f[0]]: !!d.checked })} />
                    ) : f[1] === "enum" ? (
                      <Select value={String(v[f[0]] ?? "")} onChange={(_, d) => setV({ ...v, [f[0]]: d.value })}>
                        <option value="">Select…</option>
                        {(f[4] ?? []).map((o) => (
                          <option key={o}>{o}</option>
                        ))}
                      </Select>
                    ) : (
                      <Input type={f[1] === "number" || f[1] === "int" ? "number" : f[1] === "date" ? "date" : f[1] === "datetime" ? "datetime-local" : "text"} value={String(v[f[0]] ?? "")} maxLength={f[3] || undefined} onChange={(_, d) => setV({ ...v, [f[0]]: d.value })} />
                    )}
                  </Field>
                </div>
              ))}
              <div className={css.full}>
                <Field label="Extra fields (JSON) - line items and anything not listed above">
                  <Textarea rows={3} value={extra} placeholder='{"lines":[{"itemId":"…","quantity":1}]}' onChange={(_, d) => setExtra(d.value)} />
                </Field>
              </div>
            </div>
            {err && <div className={css.err} role="alert">{err}</div>}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button appearance="primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

const GenericPage: React.FC<{ module: string; say: (m: string, err?: boolean) => void }> = ({ module, say }) => {
  const [cat, setCat] = useState<Catalog | null>(null);
  const [resIdx, setResIdx] = useState(0);
  const [q, setQ] = useState("");
  const [pg, setPg] = useState(1);
  const [items, setItems] = useState<Rec[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [sel, setSel] = useState<Rec | null>(null);
  const [form, setForm] = useState<"new" | "edit" | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actForm, setActForm] = useState<A | null>(null);
  const [actVals, setActVals] = useState<Record<string, string>>({});

  useEffect(() => void loadCatalog().then(setCat), []);
  useEffect(() => {
    setResIdx(0);
    setPg(1);
    setSel(null);
    setQ("");
  }, [module]);
  const list = useMemo(() => cat?.[module] ?? [], [cat, module]);
  const res = list[resIdx];

  const load = useCallback(async () => {
    if (!res) return;
    setLoading(true);
    setError("");
    try {
      const p = await api.page(res.r, { page: pg, pageSize: 25, q: q || undefined });
      setItems(p.items);
      setTotal(p.total);
      setPages(p.totalPages);
    } catch (e) {
      setItems([]);
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [res, pg, q]);
  useEffect(() => void load(), [load]);

  if (!cat)
    return (
      <Skeleton aria-label="Loading" style={{ padding: 16, display: "grid", gap: 14 }}>
        {Array.from({ length: 8 }, (_, i) => <SkeletonItem key={i} size={16} />)}
      </Skeleton>
    );
  if (!res) return <div className={css.empty}>This module has no resources.</div>;
  const cols = listCols(res);

  const run = async (verb: string, body?: Record<string, unknown>) => {
    if (!sel) return;
    try {
      const out = await api.post(`${res.r}/${sel.id}/${verb}`, body);
      say(`${label(verb)} done`);
      setSel(out && typeof out === "object" && (out as Rec).id === sel.id ? (out as Rec) : null);
      await load();
    } catch (e) {
      say(errText(e), true);
    }
  };
  const remove = async () => {
    if (!sel || !window.confirm(`Delete this ${res.e}?`)) return;
    try {
      await api.del(`${res.r}/${sel.id}`);
      say("Deleted");
      setSel(null);
      await load();
    } catch (e) {
      say(errText(e), true);
    }
  };

  return (
    <>
      <div className={css.bar}>
        <Select className={css.resSel} value={String(resIdx)} aria-label="Resource" onChange={(_, d) => (setResIdx(Number(d.value)), setPg(1), setSel(null))}>
          {list.map((r, i) => (
            <option key={r.r} value={i}>{title(r)}</option>
          ))}
        </Select>
        <SearchBox className={css.search} placeholder="Search" value={q} onChange={(_, d) => (setQ(d.value), setPg(1))} />
        <span className={css.grow} />
        {res.c && <Button appearance="primary" icon={<Add20Regular />} onClick={() => setForm("new")}>New</Button>}
        {res.c && <Button appearance="subtle" icon={<Edit20Regular />} disabled={!sel} onClick={() => setForm("edit")}>Edit</Button>}
        {res.c && <Button appearance="subtle" icon={<Delete20Regular />} disabled={!sel} onClick={remove}>Delete</Button>}
        {(res.a ?? []).map((a) => (
          <Button key={a[0]} appearance="subtle" disabled={!sel} onClick={() => (a[1].length ? (setActForm(a), setActVals({})) : run(a[0]))}>{label(a[0])}</Button>
        ))}
        <Button appearance="subtle" icon={<ArrowClockwise20Regular />} aria-label="Refresh" onClick={load} />
      </div>
      {error && <div className={`${css.banner} ${css.bannerOff}`} role="alert">{error}</div>}
      <div className={css.body}>
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr>{cols.map((c) => <th key={c[0]}>{label(c[0])}</th>)}</tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && Array.from({ length: 8 }, (_, i) => (
                <tr key={`sk${i}`}>
                  {cols.map((c) => <td key={c[0]}><Skeleton><SkeletonItem size={16} /></Skeleton></td>)}
                </tr>
              ))}
              {items.map((it) => (
                <tr key={it.id} aria-selected={sel?.id === it.id} tabIndex={0} onClick={() => setSel(it)} onKeyDown={(e) => e.key === "Enter" && setSel(it)}>
                  {cols.map((c) => <td key={c[0]}>{shown(it[c[0]])}</td>)}
                </tr>
              ))}
              {!loading && items.length === 0 && !error && (
                <tr><td colSpan={cols.length} className={css.empty}>No records.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {sel && (
          <aside className={css.detail} aria-label="Details">
            <strong>{shown(sel.number ?? sel.code ?? sel.name ?? sel.id)}</strong>
            <dl style={{ margin: "8px 0 0" }}>
              {Object.entries(sel).filter(([k]) => k !== "id" || true).map(([k, v]) => (
                <div key={k} className={css.detailRow}><dt>{label(k)}</dt><dd>{shown(v)}</dd></div>
              ))}
            </dl>
          </aside>
        )}
      </div>
      <div className={css.foot}>
        <span>{loading ? "Loading…" : `${total} records`}</span>
        <Button size="small" disabled={pg <= 1} onClick={() => setPg(pg - 1)}>Previous</Button>
        <span>Page {pg} of {pages}</span>
        <Button size="small" disabled={pg >= pages} onClick={() => setPg(pg + 1)}>Next</Button>
      </div>

      {form && <Editor res={res} rec={form === "edit" ? sel : null} onClose={() => setForm(null)} onDone={() => (setForm(null), say("Saved"), void load())} />}

      <Dialog open={!!actForm} onOpenChange={(_, d) => !d.open && setActForm(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{actForm ? label(actForm[0]) : ""}</DialogTitle>
            <DialogContent>
              <div className={css.form}>
                {(actForm?.[1] ?? []).map((f) => (
                  <Field key={f[0]} label={label(f[0])}>
                    <Input value={actVals[f[0]] ?? ""} onChange={(_, d) => setActVals({ ...actVals, [f[0]]: d.value })} />
                  </Field>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger action="close"><Button>Cancel</Button></DialogTrigger>
              <Button
                appearance="primary"
                onClick={() => {
                  if (!actForm) return;
                  const body: Record<string, unknown> = {};
                  for (const f of actForm[1]) {
                    const raw = actVals[f[0]];
                    if (raw === undefined || raw === "") continue;
                    body[f[0]] = f[1] === "number" || f[1] === "int" ? Number(raw) : f[1] === "bool" ? raw === "true" : raw;
                  }
                  const verb = actForm[0];
                  setActForm(null);
                  void run(verb, body);
                }}
              >
                Run
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default GenericPage;